import EventClient from "@2u-landing-pages/utils/event-client";
import { handlePIIFields } from "@2u-landing-pages/utils/formatting";
import { logEvent } from "@red-digital/bricks";
import { useEffect, useRef, useState } from "react";

type Steps = {
  current: number | null;
  total: number | null;
};

declare global {
  interface Window {
    cwr: any;
  }
}

const AUTOFILL_START_ANIMATION = "on-autofill-start";
const AUTOFILL_CANCEL_ANIMATION = "on-autofill-cancel";

// Tracks fields for the autofill CSS animation (libs/styles/base-theme.scss)
export function createAutofillTracker() {
  const autofilledFields = new WeakSet<Element>();

  return {
    isAutofilled(el: HTMLElement): boolean {
      return autofilledFields.has(el);
    },
    onAnimationStart(event: AnimationEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (event.animationName === AUTOFILL_START_ANIMATION) {
        autofilledFields.add(target);
      } else if (event.animationName === AUTOFILL_CANCEL_ANIMATION) {
        autofilledFields.delete(target);
      }
    },
  };
}

/** Logic to get the plugin mount element and differentiate duplicate '#__validation_errors' ids across hero and drawer for Keyboard navigation. */
function getPluginMount(root: HTMLElement | null, formId: string): HTMLElement | null {
  if (!root) return null;
  try {
    return root.querySelector<HTMLElement>(`#${CSS.escape(formId)}`);
  } catch {
    return root.querySelector<HTMLElement>(".self-mount-plugin");
  }
}

export const checkSubmitError = (formContainerRef: React.RefObject<HTMLDivElement>, formId: string, step?: number) => {
  const mount = getPluginMount(formContainerRef?.current ?? null, formId);
  const submitErrors = mount?.querySelector("#__validation_errors");

  if (submitErrors) {
    const errorMessages = submitErrors.querySelectorAll("a.u--text-danger");
    const errorMessage =
      Array.from(errorMessages)
        .map((el) => el.textContent?.trim())
        .filter((text) => text && text.length > 0)
        .join(", ") || "Form submission failed";
    return errorMessage;
  } else {
    return null;
  }
};

export const handleSubmitEvents = (
  formId: string,
  step?: number,
  totalSteps?: number,
  formType: "TY" | "LP" = "LP",
  location?: string
) => {
  EventClient.formSubmitted({
    formContext: {
      formType: `${formType === "TY" ? "TYP" : "LP"}${location ? ` - ${location}` : ""}`,
      formId,
    },
    stepContext: {
      stepNumber: step as number,
      ...(step === totalSteps && { stepName: "COMPLETED" }),
    },
  });

  logEvent("form_completed", {
    formId,
    complete: true,
  });

  logEvent("form_next_button", {
    formId,
    currentStep: step,
    totalSteps,
  });
};

export const useFormEvents = (formId: string, location?: string) => {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [steps, setSteps] = useState<Steps>({
    current: null,
    total: null,
  });

  const getLeadFormStep = () => {
    if (!formContainerRef.current) return;

    const label = formContainerRef.current.querySelector(".progress__label");

    if (!label) {
      setSteps({
        current: 1,
        total: 1,
      });

      return {
        current: 1,
        total: 1,
      };
    }

    const stepString = label?.textContent;
    const regex = /(\d+) of (\d+)/;
    const match = stepString?.match(regex) || [];
    const updatedSteps = {
      current: parseInt(match[1], 10) || 1,
      total: parseInt(match[2], 10) || 1,
    };

    setSteps(updatedSteps);

    return updatedSteps;
  };

  useEffect(() => {
    if (!formContainerRef.current) return;

    const root = formContainerRef.current;
    const autofillTracker = createAutofillTracker();

    const handleBlurEvent = (event: FocusEvent) => {
      const originalTarget = event.target as HTMLInputElement;
      const { current } = getLeadFormStep() || {};

      if (originalTarget?.tagName === "INPUT" && ["text", "email", "tel"].includes(originalTarget.type)) {
        // Check autofill on the live DOM node before PII sanitization clones it
        const autofilled = autofillTracker.isAutofilled(originalTarget);
        const target = handlePIIFields(originalTarget);
        EventClient.fieldInputted({
          formContext: {
            formId,
          },
          userInputField: {
            autofilled,
            fieldId: target.id,
            fieldLabel: target.placeholder,
            fieldName: target.name,
            fieldType: target.type,
            fieldValue: target.value,
          },
          stepContext: {
            stepNumber: current,
          },
        });
      }
    };

    const handleChangeEvent = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const { current } = getLeadFormStep() || {};

      if (
        target?.tagName === "SELECT" ||
        (target?.tagName === "INPUT" && !["text", "email", "tel", "password"].includes(target.type)) ||
        target?.type === "radio" ||
        target?.type === "checkbox"
      ) {
        EventClient.fieldInputted({
          formContext: {
            formId,
          },
          userInputField: {
            autofilled: autofillTracker.isAutofilled(target),
            fieldId: target.id,
            fieldLabel: target.placeholder,
            fieldName: target.name,
            fieldType: target.type,
            fieldValue: target.value,
          },
          stepContext: {
            stepNumber: current,
          },
        });
      }
    };

    const handleClickEvent = (event: MouseEvent) => {
      const target = event.target as HTMLInputElement | HTMLAnchorElement;

      if (
        target?.classList.contains("lead-form-next-step") ||
        (target?.classList.contains("button") && target?.closest(".form-get-info")) ||
        (target?.classList.contains("form-button") && target?.closest(".multi-step-form"))
      ) {
        const { innerText } = target;

        // Capture step BEFORE click for event tracking
        const { current: stepBeforeClick, total: totalSteps } = getLeadFormStep() || {};
        // Form is complete when clicking from the last step (submit button)
        const complete = stepBeforeClick === totalSteps;

        // wait for the form to update before checking for errors
        setTimeout(() => {
          if (!formContainerRef.current) return;

          const inputErrors = Array.from(
            formContainerRef.current.querySelectorAll(
              "input.error, select.error, .form-group.is--invalid, .form-field-group.invalid"
            )
          );

          if (inputErrors.length) {
            const errorMessages = formContainerRef.current.querySelectorAll(".is--invalid");
            const errorMessage =
              Array.from(errorMessages)
                .map((el) => el.textContent)
                .join(", ") ?? "";

            EventClient.formErrored({
              errorMessage,
              formContext: {
                formType: `LP${location ? ` - ${location}` : ""}`,
                formId,
              },
              stepContext: {
                stepNumber: stepBeforeClick as number,
              },
            });

            return;
          }

          EventClient.elementClicked({
            webElement: {
              htmlId: formId,
              text: innerText || "",
              position: `Step: ${stepBeforeClick}`,
              location: `LP${location ? ` - ${location}` : ""}`,
            },
          });

          handleSubmitEvents(formId, stepBeforeClick, totalSteps, "LP", location);

          if (complete) {
            setTimeout(() => {
              const submitError = checkSubmitError(formContainerRef, formId);

              if (submitError) {
                EventClient.formErrored({
                  errorMessage: submitError,
                  formContext: {
                    formType: `LP${location ? ` - ${location}` : ""}`,
                    formId,
                  },
                });
              }
            }, 2000);

            return;
          }
        });
      } else if (target?.tagName === "A") {
        EventClient.elementClicked({
          webElement: {
            elementType: "Landing Page",
            htmlId: (target as HTMLAnchorElement).href,
            location: "LoadForm",
            name: target.innerText,
            position: "",
            text: "",
          },
        });
      }
    };

    root.addEventListener("animationstart", autofillTracker.onAnimationStart, true);
    root.addEventListener("blur", handleBlurEvent, true);
    root.addEventListener("change", handleChangeEvent, true);
    root.addEventListener("click", handleClickEvent, true);

    return () => {
      root.removeEventListener("animationstart", autofillTracker.onAnimationStart, true);
      root.removeEventListener("blur", handleBlurEvent, true);
      root.removeEventListener("change", handleChangeEvent, true);
      root.removeEventListener("click", handleClickEvent, true);
    };
  }, []);

  return {
    formContainerRef,
    steps,
  };
};
